import { useEffect, useMemo, useState } from 'react';
import { useLazyLoadQuery, graphql, useMutation } from 'react-relay';
import { DataGrid, GridCellParams, GridPaginationModel, GridRenderCellParams, GridRowHeightParams, GridRowId, GridRowSelectionModel, GridToolbarColumnsButton, GridToolbarContainer } from '@mui/x-data-grid';
import { AdPerformanceTableQuery, AdPerformanceTableQuery$variables, KPI } from './__generated__/AdPerformanceTableQuery.graphql';
import { useAdSearchQueries } from '../../hooks/useGlobalSearchQueries';
import { useGlobalState } from '../../../../state/v2/store';
import { useAdsFilterMenuParams } from '../FilterMenu/useFilterMenuParams'
import { FilterBar } from '../FilterBar/FilterBar';
import { metricFilterFieldKey, KpiDisplay, currencyFormat } from '../../../../display';
import { Button } from '../../../../components/Button/Button';
import { Box, Chip, CircularProgress, Modal, TextField, Tooltip, styled } from '@mui/material';
import { theme } from '../../../../mui'
import { AdPerformanceTableTagsMutation } from './__generated__/AdPerformanceTableTagsMutation.graphql';
import { toast } from 'react-toastify'
import { AdPerformanceTableInsightsMutation } from './__generated__/AdPerformanceTableInsightsMutation.graphql';
import { CookieType, cookies } from '../../../../state/cookies';
import { CurrencyType } from '../TopPerformingAds/__generated__/TopPerformingAdsQuery.graphql';
import { removeMetricFromFilter } from '../../../../common';
interface Ad {
  brandId: string | null
  id: string | null
  name: string | null
  externalId: string | null
  primaryText: string | null
  tags: string[] | null

  rate: string | null
  amountSpent: string | null
  impressions: string | null
  landingPageViews: string | null
  websitePurchases: string | null
  websiteAddToCarts: string | null
  costPerPurchase: string | null
  reach: string | null
}

const TextCell: React.FC<{ value: string }> = ({ value }) => (
  <div
    style={{
      whiteSpace: 'normal',
      overflowWrap: 'anywhere',
      padding: '10px',
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      paddingLeft: '0px'
    }}
  >
    {value}
  </div>
);


function parseFloat(stat: number | null, currencyType?: CurrencyType) {
  if (stat === null) {
    return null
  }

  if (currencyType) {
    return currencyFormat(stat, currencyType)
  }

  return stat.toLocaleString()
}

const S = {
  TooltipContent: styled('div')({
    borderRadius: '4px',
    fontSize: '0.875rem',
    maxWidth: '250px',
    flexWrap: 'wrap',
  }),
};

const StyledTagsCell: React.FC<{ value: string[] }> = ({ value }) => (
  <Tooltip
    title={
      <S.TooltipContent>
        {value.map((item, index) => (
          <Chip key={index} label={item} style={{ backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText, margin: '2px' }} />
        ))}
      </S.TooltipContent>
    }
    placement='right'
  >
    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', padding: '10px', paddingLeft: '0px' }}>
      {value.map((item, index) => (
        <Chip key={index} label={item} style={{ backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText, margin: '2px' }} />
      ))}
    </div>
  </Tooltip>);

interface ToastWithLinkProps {
  url: string
}

const ToastWithLink = ({ url }: ToastWithLinkProps) => (
  <div>
    Successfully updated insights.
    <a href={url} target="_blank" rel="noopener noreferrer" className="text-trueBlue mt-2 block font-medium uppercase">
      <strong>View tag insights</strong>
    </a>
  </div>
)

const showToastWithLink = (url: string) => {
  toast.info(<ToastWithLink url={url} />, {
    // wait longer since the user will need to click it
    autoClose: 10000,
  })
}


const metaAdTagsMutation = graphql`
  mutation AdPerformanceTableTagsMutation(
    $input: [UpdateMetaAdRequest!]!
  ) {
    updateMetaAds(input: $input) {
      data {
        id
        tags
      }
    }
  }
`

const insightsV2Mutation = graphql`
  mutation AdPerformanceTableInsightsMutation(
    $input: ListInsightV2Request!
  ) {
    generateInsightV2(input: $input) {
      count
    }
  }
`
interface AdPerformanceTableProps {
  purchasesEnabled: boolean
  leadsEnabled: boolean
}

const AdPerformanceTable = ({purchasesEnabled, leadsEnabled}: AdPerformanceTableProps) => {
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowId[]>([]);
  const [ads, setAds] = useState<Ad[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [openTagModal, setOpenTagModal] = useState(false);
  const [editedValue, setEditedValue] = useState('');
  const [selectedTagMap, setSelectedTagMap] = useState<{ [key: string]: string[] }>({});
  const [updateMetaAdTags, updateMetaAdTagsLoading] =
    useMutation<AdPerformanceTableTagsMutation>(metaAdTagsMutation)

  const [updateInsights, updateInsightsLoading] =
    useMutation<AdPerformanceTableInsightsMutation>(insightsV2Mutation)

  const handleOpenModal = () => {
    setOpenTagModal(true);
  };

  const handleCloseModal = () => {
    setOpenTagModal(false);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditedValue(event.target.value);
  };

  const brandId = useGlobalState((b) => b.brand?.map((brand) => brand.id).join(","))

 const handleSaveChanges = () => {
  const newCommonTags = editedValue.split(',').map(tag => tag.trim()).filter(tag => tag !== '')

  // determine existing common tags and see if any were removed 
  const allTagsInMap = Object.values(selectedTagMap).flat();
  const allLowerTagsInMap = allTagsInMap.map(tag => tag.toLowerCase());
  const commonLowerTagsInMap = Object.keys(allLowerTagsInMap.reduce<{ [key: string]: number }>((acc, tag) => {
    acc[tag] = (acc[tag] || 0) + 1;
    return acc;
  }, {})).filter(tag => Object.values(selectedTagMap).every(tags => tags.some(t => t.toLowerCase() === tag)));
  const newLowerCommonTags = newCommonTags.map(tag => tag.toLowerCase());
  const tagsInAllSelectedTagMap = commonLowerTagsInMap.filter(tag =>
    Object.values(selectedTagMap).every(tags => tags.some(t => t.toLowerCase() === tag))
  );
  const tagsToRemove = tagsInAllSelectedTagMap.filter(tag => !newLowerCommonTags.includes(tag));

  const updatedTags = rowSelectionModel.map((row) => {
    const adId = row.toString()
    const currentTags = selectedTagMap[adId] || []
    const currentTagsLowerCase = currentTags.map(tag => tag.toLowerCase());
    const tagsToAdd = newCommonTags.filter(tag => !currentTagsLowerCase.includes(tag.toLowerCase()));
    const existingTags = currentTags.filter(tag => !tagsToRemove.includes(tag.toLowerCase()));
    return {
      id: adId,
      tags: [...existingTags, ...tagsToAdd],
    }
  })

  // Helper function to chunk array into smaller arrays
  const chunkArray = (array: any[], chunkSize: number) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  };

  // Split updatedTags into chunks of 10
  const tagChunks = chunkArray(updatedTags, 10);
  let completedBatches = 0;

  const onBatchCompleted = () => {
    completedBatches++;
    
    // Only proceed if all batches completed successfully and no errors occurred
    if (completedBatches === tagChunks.length) {
      toast.success('Successfully added tags');
      handleCloseModal();
    
      if (rowSelectionModel.length === 0) {
        toast.error('No rows selected.');
        return;
      }
    
      const selectedRows = ads.filter(ad => rowSelectionModel.includes(ad?.id || ''));
      const brandIds = selectedRows.map(row => row.brandId);
    
      if (brandIds.length === 0) {
        onInsightError(new Error('No valid brand IDs found.'));
        return;
      }
    
      adKpi && updateInsights({
        variables: {
          input: {
            filter: {
              brandId: brandIds.join(","),
              labelTypes: ['TAG'],
              kpi: adKpi as KPI,
              includeAllDates: true,
            }
          }
        }, 
        onCompleted: onInsightCompleted, 
        onError: onInsightError
      });
    }
  };
  
  const onBatchError = (error: Error, batchIndex: number) => {
    console.log(`Batch ${batchIndex + 1} error:`, error);
    toast.error(`Unable to update tags.`);
  };

  // Execute all batches
  tagChunks.forEach((chunk, index) => {
    updateMetaAdTags({
      variables: {
        input: chunk
      },
      onCompleted: onBatchCompleted,
      onError: (error: Error) => onBatchError(error, index),
    });
  });

  const onInsightCompleted = () => {
    // modify existing URL to show the tags
    const currentUrl = new URL(window.location.href)
    currentUrl.pathname = '/insights'
    const searchParams = currentUrl.searchParams
    searchParams.set('labelType', 'TAG')
    const newUrl = currentUrl.toString()
    // make sure the view switches to the table view since that's where tags are relevant 
    cookies.set(CookieType.INSIGHTS_VIEW, 'table')
    !updateInsightsLoading && showToastWithLink(newUrl)
  }
  
  const onInsightError = (error: Error) => {
    console.log(error)
    toast.error(`Unable to update insights.`)
  }
};


  const [
    {
      topAdMetricsFilters,
      adKpi,
      performanceDateGte,
      performanceDateLte
    },
    setParams,
  ] = useAdsFilterMenuParams()

  const searchQueries = useAdSearchQueries()
  const filteredTopAdMetricsFilters = removeMetricFromFilter(adKpi as KPI, topAdMetricsFilters);

  const tableQueryVariables = useMemo(() => ({
    skip: !brandId,
    input: {
      filter: {
        brandId: brandId!,
        searchQueries,
        metrics: filteredTopAdMetricsFilters,
        deduplicateAdNames: false,
        filterOutZero: false,
        performanceDateRange:
          performanceDateGte && performanceDateLte
            ? {
              start: performanceDateGte,
              end: performanceDateLte,
            }
            : undefined,
      },
      config: {
        kpi: adKpi as KPI,
      },
      pagination: {
        skip: paginationModel.page * paginationModel.pageSize,
        take: paginationModel.pageSize,
      },
    },
  }) as AdPerformanceTableQuery$variables, [brandId, topAdMetricsFilters, performanceDateGte, performanceDateLte, adKpi, paginationModel, searchQueries])

  const res = useLazyLoadQuery<AdPerformanceTableQuery>(
    graphql`
      query AdPerformanceTableQuery($input: ListPerformanceSummarizedMetaAdsRequest!, $skip: Boolean!) {
        listPerformanceSummarizedMetaAds(input: $input) @skip(if: $skip) {
          count
          metadata {
            currencyType
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
          }
          data {
            id
            amountSpent
            externalId
            name
            primaryText
            rate
            impressions
            landingPageViews
            websitePurchases
            websiteAddToCarts
            amountSpent
            costPerPurchase
            reach
            tags
            costPerWebsitePurchase
            purchases
            brandId
          }
        }
      }
    `, tableQueryVariables, { fetchPolicy: 'network-only' }
  );


  const handlePaginationModelChange = (model: GridPaginationModel) => {
    setPaginationModel({ page: model.page, pageSize: model.pageSize })
  }

  const handleRowSelectionModelChange = (model: GridRowSelectionModel) => {
    const selectedTags: string[] = [];
    const tagCounts: { [key: string]: number } = {};
    const tagMap: { [key: string]: string[] } = {};
    let adsWithTags = 0;
    // Determine which tags are common across all the selected ads
    model.forEach((id: GridRowId) => {
      const selectedAd = ads.find(ad => ad.id === id);
      if (selectedAd && selectedAd.id && selectedAd.tags) {
        tagMap[selectedAd.id] = selectedAd.tags
        adsWithTags += 1;
        selectedTags.push(...selectedAd.tags);
        selectedAd.tags.forEach(tag => {
          const lowercaseTag = tag.toLowerCase();
          tagCounts[lowercaseTag] = (tagCounts[lowercaseTag] || 0) + 1;
        });
      }
    });

    setSelectedTagMap(tagMap)

    const commonTags = Object.keys(tagCounts).filter(tag => tagCounts[tag] === adsWithTags);

    setEditedValue(commonTags.join(', '));
    setRowSelectionModel(Array.isArray(model) ? model : [model]);
  };

  useEffect(() => {
    if (res.listPerformanceSummarizedMetaAds?.data) {
      setAds(res.listPerformanceSummarizedMetaAds.data.map((row) => {
        return {
          id: row.id, brandId: row.brandId, amountSpent: parseFloat(row.amountSpent, res.listPerformanceSummarizedMetaAds?.metadata.currencyType || "USD") || parseFloat(0, res.listPerformanceSummarizedMetaAds?.metadata.currencyType || "USD"),
          externalId: row.externalId, name: row.name, primaryText: row.primaryText, tags: row.tags ? [...row.tags] : [],
          rate: row.rate || '-', impressions: parseFloat(row.impressions) || '0', landingPageViews: parseFloat(row.landingPageViews) || '0',
          websitePurchases: (parseFloat(purchasesEnabled ? row.purchases :row.websitePurchases)) || '0', websiteAddToCarts: parseFloat(row.websiteAddToCarts) || '0',
          reach: parseFloat(row.reach) || '0', costPerPurchase: (parseFloat(purchasesEnabled ? row.costPerPurchase : row.costPerWebsitePurchase, res.listPerformanceSummarizedMetaAds?.metadata.currencyType || "USD"))  || '-'
        }
      }));
      setTotalCount(res.listPerformanceSummarizedMetaAds.count);
    }
  }, [res])


  const columns = [
    { field: 'id', headerName: 'ID', width: 100, disableExport: true },
    { field: 'brandId', headerName: 'BrandId', width: 100, disableExport: true },
    { field: 'name', headerName: 'Ad Name', width: 250, hideable: false, renderCell: (params: GridCellParams) => <TextCell value={params.value as string} /> },
    { field: 'tags', headerName: 'Tags', width: 150, renderCell: (params: GridRenderCellParams<any, any>) => <StyledTagsCell value={params.value as string[]} /> },
    { field: 'externalId', headerName: 'Meta Ad ID', width: 150, renderCell: (params: GridCellParams) => <TextCell value={params.value as string} /> },
    { field: 'primaryText', headerName: 'Primary Text', width: 500, renderCell: (params: GridCellParams) => <TextCell value={params.value as string} /> },
    // rate is a special field representing the selected KPI
    { field: 'rate', headerName: adKpi ? KpiDisplay[adKpi as KPI] : 'KPI', width: 200, renderCell: (params: GridCellParams) => <TextCell value={params.value as string} /> },
    { field: 'amountSpent', headerName: 'Amount Spent', width: 150, renderCell: (params: GridCellParams) => <TextCell value={params.value as string} /> },
    { field: 'impressions', headerName: 'Impressions', width: 150, renderCell: (params: GridCellParams) => <TextCell value={params.value as string} /> },
    { field: 'landingPageViews', headerName: 'Landing Page Views', width: 150, renderCell: (params: GridCellParams) => <TextCell value={params.value as string} /> },
    { field: 'websitePurchases', headerName: purchasesEnabled ? 'Purchases' :'Website Purchases', width: 150, renderCell: (params: GridCellParams) => <TextCell value={params.value as string} /> },
    { field: 'websiteAddToCarts', headerName: 'Website Add to Carts', width: 150, renderCell: (params: GridCellParams) => <TextCell value={params.value as string} /> },
    { field: 'costPerPurchase', headerName: purchasesEnabled ? 'Cost Per Purchase' :'Cost Per Website Purchase', width: 150, renderCell: (params: GridCellParams) => <TextCell value={params.value as string} /> },
    { field: 'reach', headerName: 'Reach', width: 150, renderCell: (params: GridCellParams) => <TextCell value={params.value as string} /> },
  ];


  function CustomToolbar() {
    //TODO https://github.com/mui/mui-x/issues/6036
    // <GridToolbarExport printOptions={{ disableToolbarButton: true }} csvOptions={{
    //   fileName: 'AdsSummary.csv',
    //   utf8WithBom: true,
    // }} />
    //   onClick={() => {
    //   if (dataMode == 'server') {
    //     const tableState = apiRef.current.state
    //     updateRows(tableState, 'all')
    //     setDataMode('client')
    //   }
    //  }
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
      </GridToolbarContainer>
    );
  }

  return (
    <div>
      <Modal open={openTagModal} onClose={handleCloseModal}>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'background.paper', boxShadow: 24, p: 4, width: 400 }}>
          <p>Edit tags for the {rowSelectionModel.length} selected items</p>
          <TextField
            value={editedValue}
            onChange={handleInputChange}
            autoFocus
            multiline
            rows={4}
            fullWidth
            placeholder="To separate tags, enter comma"
            variant="outlined"
            size="medium"
          />
          <Button variant='blue' onClick={handleSaveChanges} disabled={updateMetaAdTagsLoading}>{updateMetaAdTagsLoading && <CircularProgress color="inherit" size={20} />} Save</Button>
        </Box>
      </Modal>

      <div className="flex items-center justify-between gap-6">
        <div>
          <p className="text-sm">
            KPI: {adKpi ? KpiDisplay[adKpi as KPI] : 'unknown'}
          </p>
          {rowSelectionModel.length > 0 && <Button variant='blue' onClick={handleOpenModal}>Add tags</Button>}
        </div>

        <FilterBar.FilterItemWrapper>
          <FilterBar.MetricFilter
            useFilterIcon={false}
            value={filteredTopAdMetricsFilters}
            handleChange={(topAdMetricsFilters) =>
              setParams({ topAdMetricsFilters }, 'pushIn')
            }
            name="metrics"
            renderOptionLabel={(opt) => metricFilterFieldKey(opt, adKpi as KPI)}
            fieldOptions={removeMetricFromFilter(adKpi ? adKpi as KPI : null, [
              'Rate',
              'AmountSpent',
              purchasesEnabled ? 'CostPerPurchase' : 'CostPerWebsitePurchase',
              'Impressions',
              'LandingPageViews',
              'LinkClicks',
              'WebsiteAddToCarts',
              purchasesEnabled ? 'Purchases' : 'WebsitePurchases',
              leadsEnabled ? 'Leads' : 'WebsiteLeads',
            ])}
          />
        </FilterBar.FilterItemWrapper>
      </div>
      <DataGrid
        rows={ads}
        columns={columns}
        disableColumnSorting
        disableColumnFilter
        pagination
        paginationMode="server"
        paginationModel={paginationModel}
        pageSizeOptions={[20, 50, 100]}
        checkboxSelection
        rowCount={totalCount}
        onPaginationModelChange={handlePaginationModelChange}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={handleRowSelectionModelChange}
        slots={{ toolbar: CustomToolbar }}
        initialState={{
          columns: {
            columnVisibilityModel: {
              // Ids is an internal column
              id: false,
              brandId: false
            },
          },
        }}
        autoHeight={true}
        getRowHeight={(params: GridRowHeightParams) => ('auto')}
      />
    </div>
  )
};

export default AdPerformanceTable;